import { useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import EastIcon from '@mui/icons-material/East';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import PersonSearchOutlinedIcon from '@mui/icons-material/PersonSearchOutlined';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';

import { PendingBookingActions } from './PendingBookingActions';
import { ReviewIdentityDialog } from './ReviewIdentityDialog';

export interface BookingGateResult {
  gate: string;
  checkpoint: string;
  outcome: string;
  reason?: string | null;
  title?: string | null;
}

export interface BookingRequestItem {
  bookingProspectId: string;
  appointmentId?: string | null;
  appointmentStatus?: string | null;
  appointmentStartTime?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  reason?: string | null;
  gateResults?: BookingGateResult[] | null;
  hasCardOnFile?: boolean;
  /** The booking re-entered review because the patient moved it (GDN-2272). */
  rescheduledByPatient?: boolean;
}

/** A patient change request awaiting a staff decision (GDN-2272). */
export interface ChangeRequestItem {
  requestId: string;
  requesterName?: string | null;
  currentStartTime?: string | null;
  requestedStartTime?: string | null;
}

interface GateChip {
  key: string;
  label: string;
  color: 'success' | 'warning';
}

/** Requirement chips: why a booking is held (paperwork/card gate state). */
function gateChips(item: BookingRequestItem): GateChip[] {
  const chips: GateChip[] = [];
  const results = item.gateResults ?? [];
  const paperwork = results.find(result => result.gate === 'paperwork');
  if (paperwork && paperwork.outcome !== 'pass') {
    chips.push({ key: 'paperwork', label: 'Paperwork pending', color: 'warning' });
  }
  const card = results.find(result => result.gate === 'card');
  if (card) {
    chips.push(
      item.hasCardOnFile || card.outcome === 'pass'
        ? { key: 'card', label: 'Card saved', color: 'success' }
        : { key: 'card', label: 'Card required', color: 'warning' },
    );
  }
  return chips;
}

export interface BookingRequestsPanelProps {
  open: boolean;
  onClose: () => void;
  items: BookingRequestItem[];
  isLoading?: boolean;
  /** Subject-language plural, e.g. "patients" / "clients". */
  subjectPlural: string;
  /** Pending patient reschedule requests (oldest first). */
  changeRequests?: ChangeRequestItem[];
  onApproveChangeRequest?: (requestId: string) => void;
  onDeclineChangeRequest?: (requestId: string) => void;
  /** Request currently being decided (disables its buttons). */
  decidingRequestId?: string | null;
  /** Surface a decision failure (e.g. the requested slot was just taken). */
  changeRequestError?: string | null;
}

function formatStart(startTime?: string | null): string {
  if (!startTime) return 'Time unavailable';
  const date = new Date(startTime);
  return date.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** Slide-over list of pending self-serve booking requests (PRD D1).
 *
 * Each row is a booking-request card — deliberately not a patient chart
 * (ADR-0077: the booker is a quarantined prospect until staff promote them).
 * Confirm/decline act on the appointment; identity review is a separate step.
 */
export function BookingRequestsPanel({
  open,
  onClose,
  items,
  isLoading = false,
  subjectPlural,
  changeRequests = [],
  onApproveChangeRequest,
  onDeclineChangeRequest,
  decidingRequestId = null,
  changeRequestError = null,
}: BookingRequestsPanelProps) {
  const [reviewProspectId, setReviewProspectId] = useState<string | null>(null);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{ paper: { sx: { width: { xs: '100%', sm: 420 } } } }}
    >
      <Box
        sx={{
          px: 3,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Booking requests
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Self-booked appointments waiting on your review
          </Typography>
        </div>
        <IconButton onClick={onClose} size="small" aria-label="Close booking requests">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <Divider />

      <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2 }}>
        {changeRequests.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              Patient requests
            </Typography>
            {changeRequestError && (
              <Alert severity="warning" sx={{ mb: 1 }}>
                {changeRequestError}
              </Alert>
            )}
            <Stack spacing={1.5}>
              {changeRequests.map(request => {
                const deciding = decidingRequestId === request.requestId;
                return (
                  <Box
                    key={request.requestId}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      p: 2,
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {request.requesterName || 'Unknown requester'} asked to
                      reschedule
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ alignItems: 'center', mt: 0.5 }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {formatStart(request.currentStartTime)}
                      </Typography>
                      <EastIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {formatStart(request.requestedStartTime)}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                      <Button
                        variant="contained"
                        size="small"
                        disabled={deciding}
                        onClick={() => onApproveChangeRequest?.(request.requestId)}
                      >
                        Approve
                      </Button>
                      <Button
                        variant="text"
                        size="small"
                        color="error"
                        disabled={deciding}
                        onClick={() => onDeclineChangeRequest?.(request.requestId)}
                      >
                        Decline
                      </Button>
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
            <Divider sx={{ mt: 2 }} />
          </Box>
        )}
        {isLoading ? (
          <Stack spacing={2}>
            <Skeleton variant="rounded" height={110} />
            <Skeleton variant="rounded" height={110} />
          </Stack>
        ) : items.length === 0 ? (
          <Stack spacing={1} sx={{ alignItems: 'center', py: 6, textAlign: 'center' }}>
            <EventBusyOutlinedIcon color="disabled" fontSize="large" />
            <Typography variant="body2" color="text.secondary">
              No pending requests — new {subjectPlural} who book online will
              show up here.
            </Typography>
          </Stack>
        ) : (
          <Stack spacing={2}>
            {items.map(item => {
              const name =
                [item.firstName, item.lastName].filter(Boolean).join(' ') ||
                'Unknown requester';
              const confirmed = item.appointmentStatus === 'scheduled';
              const chips = gateChips(item);
              return (
                <Box
                  key={item.bookingProspectId}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    p: 2,
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ alignItems: 'center', justifyContent: 'space-between' }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {name}
                    </Typography>
                    <Stack direction="row" spacing={0.75}>
                      {item.rescheduledByPatient && (
                        <Chip
                          size="small"
                          label="Rescheduled by patient"
                          color="info"
                          variant="outlined"
                        />
                      )}
                      <Chip
                        size="small"
                        label={confirmed ? 'Confirmed' : 'Pending'}
                        color={confirmed ? 'success' : 'warning'}
                        variant="outlined"
                      />
                    </Stack>
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {formatStart(item.appointmentStartTime)}
                  </Typography>
                  {chips.length > 0 && (
                    <Stack
                      direction="row"
                      spacing={0.75}
                      sx={{ mt: 0.75, flexWrap: 'wrap' }}
                    >
                      {chips.map(chip => (
                        <Chip
                          key={chip.key}
                          size="small"
                          label={chip.label}
                          color={chip.color}
                          variant="outlined"
                        />
                      ))}
                    </Stack>
                  )}
                  {(item.email || item.phone) && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block' }}
                    >
                      {[item.email, item.phone].filter(Boolean).join(' · ')}
                    </Typography>
                  )}
                  {item.reason && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', fontStyle: 'italic' }}
                    >
                      “{item.reason}”
                    </Typography>
                  )}
                  <Box sx={{ mt: 1.5 }}>
                    {!confirmed ? (
                      <PendingBookingActions
                        appointmentId={item.appointmentId}
                        bookingProspectId={item.bookingProspectId}
                        source="queue"
                        compact
                        onReviewIdentity={() =>
                          setReviewProspectId(item.bookingProspectId)
                        }
                      />
                    ) : (
                      <Button
                        variant="text"
                        size="small"
                        startIcon={<PersonSearchOutlinedIcon />}
                        onClick={() =>
                          setReviewProspectId(item.bookingProspectId)
                        }
                      >
                        Review identity
                      </Button>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Stack>
        )}
      </Box>
      {reviewProspectId && (
        <ReviewIdentityDialog
          bookingProspectId={reviewProspectId}
          open
          onClose={() => setReviewProspectId(null)}
          source="queue"
        />
      )}
    </Drawer>
  );
}
